'use client';

import type { ReactNode } from 'react';
import { Icon } from '@/components/ui/icon';
import {
  Alert01Icon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  InformationCircleIconSolid,
} from '@/components/ui/icon/glyphs';
import { cn } from '@/lib/ui/utils';

export type StatusBannerTone = 'success' | 'warning' | 'info' | 'destructive';

export interface StatusBannerProps {
  /** Visual tone — drives background tint, border, icon, and text colors. */
  tone: StatusBannerTone;
  /** Bold heading shown next to the icon. */
  title?: ReactNode;
  /** Body copy shown below the title. Optional — pass only one of these or
   *  `children` for terse one-liners. */
  description?: ReactNode;
  /** Optional override for the leading icon. Defaults to a tone-appropriate
   *  glyph (check / alert / info / cancel). */
  icon?: React.ComponentProps<typeof Icon>['icon'];
  /** Extra classes for the outer container. */
  className?: string;
  /** When passed, replaces the `title`/`description` body. Use for richer
   *  content (lists, links). */
  children?: ReactNode;
}

interface ToneSpec {
  container: string;
  iconColor: string;
  titleColor: string;
  bodyColor: string;
  defaultIcon: React.ComponentProps<typeof Icon>['icon'];
}

const TONE_SPECS: Record<StatusBannerTone, ToneSpec> = {
  success: {
    container: 'border-feedback-success-border/30 bg-feedback-success-surface',
    iconColor: 'text-feedback-success-accent',
    titleColor: 'text-feedback-success-content',
    bodyColor: 'text-feedback-success-content/80',
    defaultIcon: CheckmarkCircle02Icon,
  },
  warning: {
    container: 'border-feedback-warning-border/30 bg-feedback-warning-surface',
    iconColor: 'text-feedback-warning-accent',
    titleColor: 'text-feedback-warning-content',
    bodyColor: 'text-feedback-warning-content/80',
    defaultIcon: Alert01Icon,
  },
  info: {
    container: 'border-feedback-info-border/30 bg-feedback-info-surface',
    iconColor: 'text-feedback-info-accent',
    titleColor: 'text-feedback-info-content',
    bodyColor: 'text-feedback-info-content/80',
    defaultIcon: InformationCircleIconSolid,
  },
  destructive: {
    container: 'border-feedback-error-border/30 bg-feedback-error-surface',
    iconColor: 'text-feedback-error-accent',
    titleColor: 'text-feedback-error-content',
    bodyColor: 'text-feedback-error-content/80',
    defaultIcon: CancelCircleIcon,
  },
};

/**
 * Inline status banner — compact tinted box with a leading icon, title,
 * and optional body copy. Four tones: success / warning / info / destructive.
 *
 * Use when you need a non-blocking, in-flow notice (form hints, post-action
 * confirmations, gentle warnings). For modal-level error/success states,
 * prefer `<Alert>`. For pill-shaped status chips, use `<StatusPill>`.
 */
export function StatusBanner({
  tone,
  title,
  description,
  icon,
  className,
  children,
}: StatusBannerProps): React.ReactNode {
  const spec = TONE_SPECS[tone];
  const resolvedIcon = icon ?? spec.defaultIcon;

  return (
    <div
      role="note"
      className={cn(
        'flex items-start gap-3 rounded-overlay-item border p-3',
        spec.container,
        className,
      )}
    >
      <Icon
        icon={resolvedIcon}
        className={cn('mt-0.5 size-4 shrink-0', spec.iconColor)}
        aria-hidden="true"
      />
      <div className="flex flex-col gap-0.5">
        {children !== undefined ? (
          children
        ) : (
          <>
            {title !== undefined ? (
              <p className={cn('text-body font-medium', spec.titleColor)}>{title}</p>
            ) : null}
            {description !== undefined ? (
              <p className={cn('text-caption', spec.bodyColor)}>{description}</p>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}
